import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  Admitted,
  AuthenticationOptions,
  AuthSession,
  ConfirmEmailChangeInput,
  ConfirmEmailVerificationInput,
  ConfirmPasswordResetInput,
  DeleteAccountInput,
  DeletionStarted,
  DomainJoinRoutesInput,
  DomainRoutesInput,
  EmailChanged,
  MintedEmailLink,
  MintEmailLinkInput,
  OidcProviderInput,
  OidcRedirect,
  OidcSignedIn,
  OidcSignInInput,
  OidcSignInStartInput,
  Passkey,
  Passkeys,
  PasskeySignInInput,
  RegistrationOptions,
  RegistrationResponseInput,
  RenamePasskeyInput,
  RequestEmailChangeInput,
  SessionsList,
  SignedIn,
  SignedUp,
  SignInInput,
  SignInMethods,
  SignInRoute,
  SignUpInput,
  SsoAdmission,
  Verified,
} from "./auth.schemas.ts";
import type { OidcCheck, OidcSettings, UpdateOidcSettingsInput } from "./oidc-settings.schemas.ts";
import type { TurnstileSettings, UpdateTurnstileSettingsInput } from "./turnstile-settings.schemas.ts";

/** What the transport knows about the request a sign-in or sign-up arrived on. */
export type SignInContext = {
  readonly userAgent: string | undefined;
  /** The browser's random id and the caller's network block, on the doors that open an account
   * (ADR-025 §5): what one welcome per browser and per network is decided on. */
  readonly browserId?: string;
  readonly addressBlock?: string;
  /** The session the door's own cookie already names, read only by a door bound with
   * `knownSession: true` (D948): a replay of an already-spent return (a reload, back or a
   * restored tab) can tell it is harmless instead of refusing someone the cookie already
   * signs in. Absent for every other door, and for this one with nobody signed in yet. */
  readonly existingUserId?: string;
};

/** `permissions` is a plain passthrough of the caller's own live set (S9.01), carried here
 * since this module resolves none of its own. `impersonatedBy` is a plain passthrough of
 * `caller.impersonating` (S9.06, ADR-005 point 6): set while the caller's session is mid
 * visit, naming who started it, for the shell's banner. */
export type SessionInput = {
  readonly userId: string | null;
  readonly permissions: readonly string[] | undefined;
  readonly impersonatedBy: { readonly actorUserId: string } | undefined;
};

export interface AuthApi extends EraseStep {
  /** Who the caller is; null when nobody is signed in. */
  session(input: SessionInput): Promise<AuthSession>;
  signIn(input: SignInInput, context?: SignInContext): Promise<SignedIn>;
  /** Creates the credentials and a fresh account and mails it a verification link; an address
   * already taken gets a notice mailed to its owner instead, and the same answer (ADR-010 rev. 3). */
  signUp(input: SignUpInput, context?: SignInContext): Promise<SignedUp>;
  /** Always answers the same and is rate limited per address, so it never reveals which emails exist. */
  requestPasswordReset(input: { readonly email: string }): Promise<{ ok: true }>;
  /** Spends the token a mailed reset link carried, sets the new password, ends every session it
   * had and starts a fresh one; throws when the token is unknown, used or expired. */
  confirmPasswordReset(input: ConfirmPasswordResetInput): Promise<SignedIn>;
  /** Mails a fresh verification link to the caller's address; rate limited. */
  requestEmailVerification(input: { readonly userId: string }): Promise<{ ok: true }>;
  /** Spends the token a mailed link carried and signs its owner in; throws when it is unknown,
   * used or expired. */
  confirmEmailVerification(input: ConfirmEmailVerificationInput): Promise<Verified>;
  /** How someone with this email signs in: password, or their company's connection. */
  signInRoute(input: { readonly email: string }): Promise<SignInRoute>;
  /** Replaces the domains a connection routes; an empty list takes it off sign-in. */
  setDomainRoutes(input: DomainRoutesInput): Promise<void>;
  /** Replaces the domains that join an organization on a verified, signed-in address
   * (S1.07): an empty list turns domain join off for this connection. Peer-only, the
   * same shape as `setDomainRoutes`; sso calls it, never the wire. */
  setDomainJoinRoutes(input: DomainJoinRoutesInput): Promise<void>;
  /** Signs in someone their identity provider vouched for, creating them if allowed. */
  admitSsoSignIn(input: SsoAdmission, context?: SignInContext): Promise<Admitted>;
  /** Ends one session; `token` names the caller's own, absent for a simulated or fallback one. */
  signOut(input: { readonly token: string | undefined }): Promise<{ ok: true }>;
  /** Every active session of the caller, newest first, flagging the one asking (S0.03). */
  listSessions(input: { readonly userId: string; readonly token: string | undefined }): Promise<SessionsList>;
  /** Ends one of the caller's own sessions; refuses an id naming someone else's (S0.03). */
  endSession(input: { readonly userId: string; readonly id: string }): Promise<{ ok: true }>;
  /** Ends every session of the caller, including the one asking (S0.03). */
  signOutEverywhere(input: { readonly userId: string }): Promise<{ ok: true }>;
  /** Sets a new password after checking the current one; ends every other session (S0.06, ADR-004 §3). */
  changePassword(input: {
    readonly userId: string;
    readonly token: string | undefined;
    readonly currentPassword: string;
    readonly newPassword: string;
  }): Promise<{ ok: true }>;
  /** Begins adding a passkey: a registration ceremony's options, with a fresh challenge the
   * server stores and checks once (ADR-004 point 4). */
  passkeyRegistrationOptions(input: { readonly userId: string }): Promise<RegistrationOptions>;
  /** Verifies the registration ceremony's response and saves the new passkey. */
  addPasskey(input: {
    readonly userId: string;
    readonly response: RegistrationResponseInput;
  }): Promise<Passkey>;
  /** This person's passkeys, newest first. */
  listPasskeys(input: { readonly userId: string }): Promise<Passkeys>;
  /** The back office's own read (S9.04, ADR-005 point 4): whether a password is set, how
   * many passkeys, and whether the address was admitted by an identity provider. Peer-only. */
  signInMethods(input: { readonly userId: string }): Promise<SignInMethods>;
  /** Renames one of this person's passkeys. */
  renamePasskey(input: { readonly userId: string } & RenamePasskeyInput): Promise<Passkey>;
  /** Removes one of this person's passkeys. */
  removePasskey(input: { readonly userId: string; readonly id: string }): Promise<{ ok: true }>;
  /** Begins signing in with a passkey: an authentication ceremony's options for any passkey this
   * site knows (a discoverable credential), so no email is asked first. */
  passkeySignInOptions(): Promise<AuthenticationOptions>;
  /** Verifies the authentication ceremony's response, checks and advances the signature
   * counter, and signs in; throws when the counter did not advance (point 4) or verification fails. */
  passkeySignIn(input: PasskeySignInInput, context?: SignInContext): Promise<SignedIn>;
  /** Checks the current password and mails a confirmation link to the new address, plus a
   * notice to the old one; nothing about the sign-in address changes until that link is
   * confirmed (S6.02, Decided in spec). Refuses when the new address is already in use. */
  requestEmailChange(input: { readonly userId: string } & RequestEmailChangeInput): Promise<{ ok: true }>;
  /** Spends a mailed confirmation link's token once, swapping the sign-in address and marking
   * it verified; throws when the token is unknown, used or expired. */
  confirmEmailChange(input: ConfirmEmailChangeInput): Promise<EmailChanged>;
  /** Starts the account's deletion grace period (S6.08, Decided in spec): refused with
   * organization's own `OrganizationLastAdminError` while the caller is the last active
   * admin of an organization that has other members. Ends every session. `confirmEmail`
   * (review group 3 security finding 8) is checked server-side against the caller's own,
   * trimmed and case-insensitive, before anything runs: refuses with
   * `ErasureConfirmationMismatchError` on a mismatch. */
  deleteAccount(input: { readonly userId: string } & DeleteAccountInput): Promise<DeletionStarted>;
  /** Begins signing in or up with Google or LinkedIn (ADR-007): stores a single-use state, a
   * nonce and, where the provider takes PKCE, a verifier, and answers the provider's address to
   * send the browser to. */
  oidcSignInStart(input: OidcSignInStartInput): Promise<OidcRedirect>;
  /** Begins connecting a provider to a signed-in account, the same way. */
  oidcLinkStart(input: OidcProviderInput & { readonly userId: string }): Promise<OidcRedirect>;
  /** Finishes either: spends the state, trades the code with the provider, checks the ID token,
   * then signs in (finding, opening or creating the account) or links the provider to whoever
   * started. */
  oidcSignIn(input: OidcSignInInput, context?: SignInContext): Promise<OidcSignedIn>;
  /** Disconnects a provider from the caller's account. */
  oidcUnlink(input: OidcProviderInput & { readonly userId: string }): Promise<{ ok: true }>;
  // Google's and LinkedIn's client, staff's to change from the back office (ADR-008). Peer-only:
  // ops checks the staff grant and keeps the audit; a secret never leaves this module.
  /** Each provider's issuer, client id and secret (set or not, last four), and where each comes from. */
  oidcSettings(): Promise<OidcSettings>;
  /** Stores or clears a provider's settings; the next sign-in uses them, with no restart. */
  updateOidcSettings(
    input: UpdateOidcSettingsInput & { readonly actorUserId: string },
  ): Promise<OidcSettings>;
  /** Asks the provider's issuer, as configured now, for its discovery document. */
  checkOidcProvider(input: OidcProviderInput): Promise<OidcCheck>;
  // Cloudflare Turnstile's keys for the sign-up challenge (ADR-025 §8, revision 2), staff's to
  // change from the back office the same way: a third provider entry, peer-only.
  /** The site key and whether the secret is set (and its last four), and where each comes from. */
  turnstileSettings(): Promise<TurnstileSettings>;
  /** Stores or clears a key; the next sign-up uses it, with no restart. */
  updateTurnstileSettings(
    input: UpdateTurnstileSettingsInput & { readonly actorUserId: string },
  ): Promise<TurnstileSettings>;
  /** Mints a fresh, single-use token for the kind named and the link that carries it, replacing
   * whatever token that kind already held for `userId` (the newest link wins); the event log
   * never holds one (ADR-022 point 7, S26.12). Peer-only, reached through worker-bridge the
   * moment a delivery is about to mail it, never notification directly (a cycle through
   * organization, which already peers notification; D500). */
  mintEmailLink(input: MintEmailLinkInput): Promise<MintedEmailLink>;
}

export const AuthApi = moduleApi<AuthApi>()("auth");
